import React, { useEffect, useState } from 'react';
import {
  createFeriado,
  CreateFeriadoPayload,
  Feriado,
  getFeriados,
  removeFeriado,
  updateFeriado,
} from '../services/feriados.service';
import { getErrorMessage } from '../utils/errorHandler';

const getTodayIso = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const emptyForm: CreateFeriadoPayload = {
  nombre: '',
  fecha: getTodayIso(),
  activo: true,
};

const FeriadosPage: React.FC = () => {
  const [feriados, setFeriados] = useState<Feriado[]>([]);
  const [form, setForm] = useState<CreateFeriadoPayload>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await getFeriados();
      setFeriados(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void cargar();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditId(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (editId) {
        await updateFeriado(editId, form);
        setSuccess('Feriado actualizado');
      } else {
        await createFeriado(form);
        setSuccess('Feriado creado (24hs)');
      }
      resetForm();
      await cargar();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (feriado: Feriado) => {
    setEditId(feriado._id);
    setForm({
      nombre: feriado.nombre,
      fecha: feriado.fecha,
      activo: feriado.activo,
    });
  };

  const onDelete = async (id: string) => {
    if (!window.confirm('Eliminar feriado?')) return;
    setProcessingId(id);
    setError('');
    setSuccess('');
    try {
      await removeFeriado(id);
      setSuccess('Feriado eliminado');
      await cargar();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <>
      <h1 className="page-title">Feriados</h1>

      <div className="card">
        <h2 className="card-title">ABM de feriados</h2>
        {error && <div className="error-msg">{error}</div>}
        {success && <div className="success-msg">{success}</div>}
        <p className="form-hint">Cada feriado impacta 24 horas en el calculo de asistencia de toda la empresa.</p>

        <form onSubmit={onSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Nombre</label>
              <input
                value={form.nombre}
                onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Fecha</label>
              <input
                type="date"
                value={form.fecha}
                onChange={(e) => setForm((prev) => ({ ...prev, fecha: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="checkbox-group">
            <input
              id="feriado-activo"
              type="checkbox"
              checked={Boolean(form.activo)}
              onChange={(e) => setForm((prev) => ({ ...prev, activo: e.target.checked }))}
            />
            <label htmlFor="feriado-activo">Activo</label>
          </div>

          <div className="actions-cell">
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Guardando...' : editId ? 'Guardar cambios' : 'Crear feriado'}
            </button>
            {editId && (
              <button className="btn btn-outline" type="button" onClick={resetForm}>
                Cancelar edicion
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="card">
        <h2 className="card-title">Listado de feriados</h2>
        {loading ? (
          <div className="loading">Cargando...</div>
        ) : feriados.length === 0 ? (
          <div className="empty-state">No hay feriados registrados.</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Nombre</th>
                  <th>Activo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {feriados.map((feriado) => (
                  <tr key={feriado._id}>
                    <td>{feriado.fecha}</td>
                    <td>{feriado.nombre}</td>
                    <td>{feriado.activo ? 'Si' : 'No'}</td>
                    <td>
                      <div className="actions-cell">
                        <button className="btn btn-outline btn-sm" type="button" onClick={() => onEdit(feriado)}>
                          Editar
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          type="button"
                          disabled={processingId === feriado._id}
                          onClick={() => onDelete(feriado._id)}
                        >
                          {processingId === feriado._id ? 'Procesando...' : 'Eliminar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default FeriadosPage;
